import { Fragment, type ReactNode } from "react";
import { marked, type Token, type Tokens } from "marked";
import { Link } from "react-router-dom";

/**
 * Markdown to React elements, never HTML strings: raw HTML in the source is shown
 * as text, links to other sites open in a new tab (the app's own pages open in
 * place) and only safe schemes are linked, images show
 * their alt text. Built on `marked`, which the Knowledge Base editor already
 * ships, so the app carries one Markdown parser instead of two.
 */
export default function Markdown({ children }: { children: string }) {
  const tokens = marked.lexer(String(children ?? ""));
  return <div className="markdown-content">{blocks(tokens)}</div>;
}

const SAFE_HREF = /^(https?:|mailto:|tel:|\/|#)/i;

function blocks(tokens: Token[]): ReactNode {
  return tokens.map((t, i) => <Fragment key={i}>{block(t)}</Fragment>);
}

function block(t: Token): ReactNode {
  switch (t.type) {
    case "heading": {
      const H = `h${(t as Tokens.Heading).depth}` as "h1";
      return <H>{inline((t as Tokens.Heading).tokens)}</H>;
    }
    case "paragraph":
      return <p>{inline((t as Tokens.Paragraph).tokens)}</p>;
    case "text":
      return (t as Tokens.Text).tokens ? inline((t as Tokens.Text).tokens!) : (t as Tokens.Text).text;
    case "list": {
      const l = t as Tokens.List;
      // "- [ ] item" is a to-do: a read-only checkbox stands in for the bullet, the item's text beside it.
      const items = l.items.map((item, i) => {
        if (!item.task) return <li key={i}>{blocks(item.tokens)}</li>;
        const box = item.tokens[0]?.type === "checkbox" ? (item.tokens[0] as Tokens.Checkbox) : null;
        return <li key={i} className="task-list-item">{box && checkbox(box)}<div>{blocks(box ? item.tokens.slice(1) : item.tokens)}</div></li>;
      });
      const tasks = l.items.some((item) => item.task) ? "contains-task-list" : undefined;
      return l.ordered ? <ol className={tasks} start={typeof l.start === "number" ? l.start : undefined}>{items}</ol> : <ul className={tasks}>{items}</ul>;
    }
    case "checkbox":
      return checkbox(t as Tokens.Checkbox);
    case "blockquote":
      return <blockquote>{blocks((t as Tokens.Blockquote).tokens)}</blockquote>;
    case "code":
      return <pre><code>{(t as Tokens.Code).text}</code></pre>;
    case "hr":
      return <hr />;
    case "table": {
      const tb = t as Tokens.Table;
      return (
        <div className="table-wrap">
          <table>
            <thead><tr>{tb.header.map((c, i) => <th key={i}>{inline(c.tokens)}</th>)}</tr></thead>
            <tbody>{tb.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{inline(c.tokens)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
    }
    case "html":
      // Shown as text, never rendered.
      return <p>{(t as Tokens.HTML).text}</p>;
    case "space":
      return null;
    default:
      return "raw" in t ? String((t as { raw: string }).raw) : null;
  }
}

function inline(tokens: Token[] = []): ReactNode {
  return tokens.map((t, i) => <Fragment key={i}>{span(t)}</Fragment>);
}

function span(t: Token): ReactNode {
  switch (t.type) {
    case "text": {
      const tx = t as Tokens.Text;
      return tx.tokens ? inline(tx.tokens) : decode(tx.text);
    }
    case "strong":
      return <strong>{inline((t as Tokens.Strong).tokens)}</strong>;
    case "em":
      return <em>{inline((t as Tokens.Em).tokens)}</em>;
    case "del":
      return <del>{inline((t as Tokens.Del).tokens)}</del>;
    case "codespan":
      return <code>{decode((t as Tokens.Codespan).text)}</code>;
    case "br":
      return <br />;
    case "checkbox":
      return checkbox(t as Tokens.Checkbox);
    case "link": {
      const l = t as Tokens.Link;
      if (!SAFE_HREF.test(l.href.trim())) return inline(l.tokens);
      // A link to one of the app's own pages opens in place; anything else opens a new tab.
      const href = l.href.trim();
      if (href.startsWith("/") && !href.startsWith("//")) return <Link to={href}>{inline(l.tokens)}</Link>;
      return <a href={href} target="_blank" rel="noopener noreferrer">{inline(l.tokens)}</a>;
    }
    case "image":
      return <span>{(t as Tokens.Image).text || "[Image]"}</span>;
    case "escape":
      return (t as Tokens.Escape).text;
    case "html":
      return (t as Tokens.Tag).text;
    default:
      return "raw" in t ? String((t as { raw: string }).raw) : null;
  }
}

/** A to-do box from "- [ ]" / "- [x]": shown, never ticked from here. */
function checkbox(t: Tokens.Checkbox): ReactNode {
  return <input type="checkbox" className="md-check" checked={t.checked} readOnly disabled aria-label={t.checked ? "Done" : "To do"} />;
}

/** marked escapes &, <, >, " and ' in inline text; React escapes on its own. */
function decode(s: string): string {
  return s.replace(/&(amp|lt|gt|quot|#39);/g, (_, e) => ({ amp: "&", lt: "<", gt: ">", quot: '"', "#39": "'" })[e as "amp"]);
}
